<script lang="ts">
	import { page } from '$app/stores';
	import { Menu, X } from 'lucide-svelte';
	import type { User } from '$lib/types/user';
	import '$lib/styles/marketing-scoped.css';

	let {
		user,
		darkMode = false,
		onToggleDarkMode = () => {},
	} = $props<{
		user?: User;
		darkMode?: boolean;
		onToggleDarkMode?: () => void;
	}>();

	let mobileMenuOpen = $state(false);

	function isCurrentPath(path: string): boolean {
		return $page.url?.pathname === path;
	}
</script>

<header class="marketing-header">
	<nav class="navbar">
		<div class="navbar-container">
			<a href="/" class="navbar-brand">
				<img loading="lazy" src="/images/2025LGLogo.png" alt="LocallyGrown" class="logo" />
				LocallyGrown.net
			</a>

			<!-- Desktop Menu -->
			<ul class="nav-menu">
				<li><a href="/" class="nav-link" class:active={isCurrentPath('/')}>Home</a></li>
				<li>
					<a href="/markets" class="nav-link" class:active={isCurrentPath('/markets')}
						>Find Markets</a
					>
				</li>
				<li>
					<a href="/features" class="nav-link" class:active={isCurrentPath('/features')}>Features</a
					>
				</li>
				<li>
					<a href="/pricing" class="nav-link" class:active={isCurrentPath('/pricing')}>Pricing</a>
				</li>
				<li>
					<a href="/faq/marketing" class="nav-link" class:active={isCurrentPath('/faq/marketing')}
						>FAQ</a
					>
				</li>
				{#if user?.superuser}
					<li>
						<a href="/admin" class="nav-link" class:active={isCurrentPath('/admin')}>Dashboard</a>
					</li>
				{/if}
				<li class="nav-cta">
					<a href="/markets/new" class="cta-link">Start Your Market</a>
				</li>
			</ul>

			<!-- Mobile Controls -->
			<div class="mobile-controls">
				<!-- Dark mode toggle button -->
				<button
					class="dark-mode-toggle"
					onclick={onToggleDarkMode}
					title="Toggle dark mode"
					aria-label="Toggle dark mode"
				>
					{darkMode ? '☀️' : '🌙'}
				</button>

				<!-- Mobile Menu Button -->
				<button
					class="mobile-menu-btn"
					onclick={() => (mobileMenuOpen = !mobileMenuOpen)}
					aria-label="Toggle menu"
					aria-expanded={mobileMenuOpen}
				>
					{#if mobileMenuOpen}
						<X size={24} />
					{:else}
						<Menu size={24} />
					{/if}
				</button>
			</div>
		</div>

		<!-- Mobile Menu -->
		{#if mobileMenuOpen}
			<div class="mobile-menu">
				<a href="/" class="mobile-menu-link" class:active={isCurrentPath('/')}>Home</a>
				<a href="/markets" class="mobile-menu-link" class:active={isCurrentPath('/markets')}
					>Find Markets</a
				>
				<a href="/features" class="mobile-menu-link" class:active={isCurrentPath('/features')}
					>Features</a
				>
				<a href="/pricing" class="mobile-menu-link" class:active={isCurrentPath('/pricing')}
					>Pricing</a
				>
				<a
					href="/faq/marketing"
					class="mobile-menu-link"
					class:active={isCurrentPath('/faq/marketing')}>FAQ</a
				>
				{#if user?.superuser}
					<a href="/admin" class="mobile-menu-link" class:active={isCurrentPath('/admin')}
						>Dashboard</a
					>
				{/if}
				<a href="/markets/new" class="mobile-menu-cta">Start Your Market</a>
			</div>
		{/if}
	</nav>
</header>

<style>
	.marketing-header {
		position: sticky;
		top: 0;
		z-index: 100;
		background: var(--marketing-bg-white, #ffffff);
	}

	/* Primary Navigation - Showcase Style */
	.navbar {
		background: var(--marketing-bg-white, #ffffff);
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
		position: relative;
	}

	.navbar-container {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 1rem 1.5rem;
		max-width: 1200px;
		margin: 0 auto;
	}

	.navbar-brand {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		text-decoration: none;
		font-weight: 700;
		font-size: 1.25rem;
		color: var(--marketing-text-primary, #466d4d);
	}

	.logo {
		height: 64px;
		width: auto;
		object-fit: contain;
	}

	.nav-menu {
		display: none;
		list-style: none;
		margin: 0;
		padding: 0;
		gap: 2rem;
		align-items: center;
	}

	@media (min-width: 768px) {
		.nav-menu {
			display: flex;
		}
	}

	.nav-link {
		text-decoration: none;
		color: var(--marketing-text-secondary, #6d8d57);
		font-weight: 500;
		padding: 0.5rem 0;
		position: relative;
		transition: color 0.2s ease;
	}

	.nav-link:hover {
		color: var(--marketing-primary, #466d4d);
	}

	.nav-link.active {
		color: var(--marketing-primary, #466d4d);
	}

	.nav-link.active::after {
		content: '';
		position: absolute;
		bottom: -0.5rem;
		left: 0;
		right: 0;
		height: 2px;
		background: var(--marketing-primary);
	}

	.nav-cta {
		margin-left: 1rem;
	}

	.nav-cta .cta-link {
		background: var(--marketing-primary, #466d4d) !important;
		color: var(--marketing-text-white, #ffffff) !important;
		padding: 0.625rem 1.25rem;
		border-radius: 6px;
		transition: all 0.2s;
		text-decoration: none;
		display: inline-block;
		font-size: 0.95rem;
		font-weight: 500;
		white-space: nowrap;
		border: 2px solid var(--marketing-primary, #466d4d);
	}

	.nav-cta .cta-link:hover {
		background: var(--marketing-primary-hover);
		border-color: var(--marketing-primary-hover);
		transform: translateY(-2px);
		box-shadow: var(--marketing-shadow-md);
	}

	.mobile-menu-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		background: transparent;
		border: none;
		padding: 0.5rem;
		cursor: pointer;
		color: var(--marketing-text-primary);
		border-radius: 0.25rem;
		transition: background 0.2s;
	}

	.mobile-menu-btn:hover {
		background: var(--marketing-bg-secondary);
	}

	@media (min-width: 768px) {
		.mobile-menu-btn {
			display: none;
		}
	}

	.mobile-menu {
		position: absolute;
		top: 100%;
		left: 0;
		right: 0;
		background: var(--marketing-bg-white);
		border-top: 1px solid var(--marketing-border-light);
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
		animation: slideDown 0.3s ease;
	}

	@keyframes slideDown {
		from {
			transform: translateY(-10px);
			opacity: 0;
		}
		to {
			transform: translateY(0);
			opacity: 1;
		}
	}

	.mobile-menu-link {
		display: flex;
		align-items: center;
		gap: 1rem;
		padding: 1rem 1.5rem;
		text-decoration: none;
		color: var(--marketing-text-primary);
		font-weight: 500;
		border-bottom: 1px solid var(--marketing-border-light);
		transition: background 0.2s ease;
	}

	.mobile-menu-link:hover {
		background: var(--marketing-bg-secondary);
	}

	.mobile-menu-link.active {
		color: var(--marketing-primary);
		background: var(--marketing-accent-light);
	}

	.mobile-menu-cta {
		display: block;
		padding: 1rem 1.5rem;
		text-decoration: none;
		background: var(--marketing-primary);
		color: var(--marketing-text-white) !important;
		font-weight: 600;
		text-align: center;
		transition: background 0.2s ease;
	}

	.mobile-menu-cta:hover {
		background: var(--marketing-primary-hover);
	}

	/* Mobile Controls Container */
	.mobile-controls {
		display: none;
		align-items: center;
		gap: 0.5rem;
	}

	@media (max-width: 768px) {
		.mobile-controls {
			display: flex;
		}
	}

	/* Dark mode toggle button */
	.dark-mode-toggle {
		background-color: transparent;
		border: none;
		border-radius: 0.25rem;
		width: 2.5rem;
		height: 2.5rem;
		font-size: 1.25rem;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		transition: all 0.2s ease;
	}

	.dark-mode-toggle:hover {
		background-color: var(--marketing-bg-secondary);
	}

	.dark-mode-toggle:active {
		transform: scale(0.95);
	}

	/* Desktop dark mode toggle */
	@media (min-width: 769px) {
		/* Show as floating button on desktop */
		.mobile-controls {
			position: fixed;
			top: 1rem;
			right: 1rem;
			z-index: 1000;
			display: flex;
		}

		.mobile-controls .dark-mode-toggle {
			background-color: var(--marketing-bg-secondary);
			border: 2px solid var(--marketing-border-medium);
			border-radius: 50%;
			width: 3rem;
			height: 3rem;
			font-size: 1.5rem;
			box-shadow: var(--marketing-shadow-md);
		}

		.mobile-controls .dark-mode-toggle:hover {
			background-color: var(--marketing-bg-tertiary);
			border-color: var(--marketing-primary);
			transform: scale(1.1);
			box-shadow: var(--marketing-shadow-lg);
		}

		.mobile-controls .mobile-menu-btn {
			display: none;
		}
	}

	@media (max-width: 768px) {
		.navbar-container {
			padding: 0.75rem 1rem;
		}
	}
</style>
